import {Alpha3, BigDecimal, Currency, UuidResponseBody} from '@dms/api/shared';

type PriceReportResponseBody = {
  id: string;
  requestId: string | null;
  url: string;
  country: Alpha3;
  createdAt: string;
};

export type CreatePresetApiResponse = UuidResponseBody;
export type SourcingCreatePresetApiArg = {
  createPresetRequestBody: CreatePresetRequestBody;
};
export type GetPresetsListApiResponse = /** status 200  */ PresetListItemResponseBody[];
export type GetPresetsListApiArg = {
  country?: Alpha3;
  currency?: Alpha3;
};
export type UpdatePresetApiResponse = unknown;
export type SourcingUpdatePresetApiArg = {
  id: string;
  updatePresetRequestBody: UpdatePresetRequestBody;
};
export type DeletePresetApiResponse = unknown;
export type SourcingDeletePresetApiArg = {
  id: string;
};
export type GetPresetApiResponse = /** status 200  */ SourcingPresetResponseBody;
export type GetPresetApiArg = {
  id: string;
};
export type ReorderPresetsApiResponse = /** status 200  */ PresetListItemResponseBody[];
export type ReorderPresetsApiArg = {
  reorderPresetsRequestBody: ReorderPresetsRequestBody;
};
export type SourcingPresetResponseBody = {
  id: string;
  name: string;
  filters: FiltersBody;
  allowNotifications: boolean;
  newCount: number | null;
};
export type FiltersAddressGpsBody = {
  lat?: BigDecimal | null;
  lng?: BigDecimal | null;
};
export type FiltersAddressBody = {
  placeId?: string | null;
  prefix?: string | null;
  street?: string | null;
  orientationNumber?: string | null;
  descriptiveNumber?: string | null;
  city?: string | null;
  zipCode?: string | null;
  postal?: string | null;
  district?: string | null;
  state?: string | null;
  country?: string | null;
  gps?: FiltersAddressGpsBody | null;
};
export type FiltersBody = {
  sellerCountry?: Alpha3[];
  sellerServer?: string[];
  sellerType?: string[];
  avgNumberAds?: number[] | null;
  make?: string[];
  model?: string[];
  fuelType?: string[];
  drive?: string[];
  transmission?: string[];
  bodyType?: string[];
  selectedColors?: {
    key: string;
    name: string;
    colorCode: string;
  }[];
  year?: number[] | null;
  power?: number[] | null;
  margin?: number[] | null;
  marginPercent?: number[] | null;
  pricePercentile?: number[] | null;
  cubicCapacity?: number[] | null;
  liquidity?: number[] | null;
  mileageRange?: number[] | null;
  daysOnDisplay?: number[] | null;
  priceCZK?: number[] | null;
  numberOfSeats?: number[] | null;
  featureScore?: number[] | null;
  reclaimableVat?: boolean | null;
  features?: string[];
  trimLevels?: string[];
  doorCount?: string[];
  adTitle?: string | null;
  address?: FiltersAddressBody | null;
  radius?: number | null;
  hideSuspicious?: boolean;
  hitlistOnly?: boolean | null;
  hideBlacklist?: boolean | null;
  carvagoAvailability?: boolean | null;
  isMarginAbsolute?: boolean;
  isPowerInKW?: boolean;
};
export type CreatePresetRequestBody = {
  name: string;
  filters: FiltersBody;
  allowNotifications: boolean;
};
export type PresetListItemResponseBody = {
  id: string;
  name: string;
  position: number;
  allowNotifications: boolean;
  newCount: number | null;
};
export type UpdatePresetRequestBody = {
  name?: string;
  filters?: FiltersBody;
  allowNotifications?: boolean;
};
export type OrderedPresetItem = {
  id: string;
  position: number;
};
export type ReorderPresetsRequestBody = {
  items: OrderedPresetItem[];
};

export type ExportAllUserToBuyToCsvApiArg = {
  buyerCountry?: Alpha3;
  currency?: Alpha3;
  margin?: string;
  orderBy?: string;
};

export type TranslationResponseBody = {
  key: string;
  translation: string;
};

export type FilterCar = {
  make?: string | null;
  model?: string | null;
  year?: number | null;
};

export type FilterColors = {
  color?: string | null;
  colorSpecification?: string | null;
};

export type FloatRange = {
  lte?: number | null;
  gte?: number | null;
};

export type FilterSeller = {
  country?: string[];
  server?: string[];
  types?: string[];
  ranking?: number | null;
};

export type IntegerRange = {
  lte?: number | null;
  gte?: number | null;
};

export type GetTopSellingRequestBody = {
  price?: IntegerRange | null;
  daysOnStock?: IntegerRange | null;
  car?: FilterCar[];
  fuelType?: string[];
  transmission?: string[];
  bodyType?: string[];
  doorCount?: string[];
  driveType?: string[];
  mileageRange?: IntegerRange | null;
  year?: IntegerRange | null;
  power?: IntegerRange | null;
  cubicCapacity?: IntegerRange | null;
  colors?: FilterColors[];
  featureScore?: FloatRange | null;
  seller?: FilterSeller | null;
};

export type SourcingGetTopSellingApiArg = {
  size?: number;
  body: GetTopSellingRequestBody;
};

export type TopSellingVehicleResponseBody = {
  count: number | null;
  make: TranslationResponseBody | null;
  model: TranslationResponseBody | null;
};

export type SourcingGetTopSellingApiResponse = /** status 200  */ TopSellingVehicleResponseBody[];

export type SourcingGetPriceReportApiResponse = /** status 200  */ PriceReportResponseBody;
export type SourcingGetPriceReportApiArg = {
  externalId?: string;
  country?: string;
};

export type UserListLinksResponseBody = {
  size: number;
  offset: number;
};

export type SourcingVehicleListItemResponseBody = {
  favourite: boolean;
  comparison: boolean;
  seen: boolean;
  isNew?: boolean; // Present in list responses, always undefined in detail responses
  inCart: boolean;
  hidden: boolean;
  carvagoBuy: boolean;
  summary: SummaryResponseBody;
  vehicleSummary: VehicleSummaryResponseBody | null;
  sourcingVehicle: SourcingVehicleResponseBody;
  features: {
    [key: string]: FeatureResponseBody;
  };
};

export type FilterBranch = {
  address?: string | null;
  zip?: string | null;
};

export type FilterBuyer = {
  country: string;
  branch?: FilterBranch | null;
};

export type AvailableCarsFilterSeller = {
  country?: string[];
  server?: string[];
  type?: string[];
  ranking?: number | null;
  numberAds?: IntegerRange | null;
  avgNumberAds?: IntegerRange | null;
};

export type AvailableCarsFilterRequestBody = {
  price?: IntegerRange | null;
  daysOnStock?: IntegerRange | null;
  car?: FilterCar[];
  features?: string[];
  fuelType?: string[];
  transmission?: string[];
  bodyType?: string[];
  doorCount?: string[];
  driveType?: string[];
  mileageRange?: IntegerRange | null;
  year?: IntegerRange | null;
  power?: IntegerRange | null;
  cubicCapacity?: IntegerRange | null;
  colors?: FilterColors[];
  featureScore?: FloatRange | null;
  trimLevels?: string[];
  seller?: AvailableCarsFilterSeller | null;
  buyer: FilterBuyer;
  margin?: IntegerRange | null;
  marginPercent?: IntegerRange | null;
  pricePercentile?: FloatRange | null;
  position?: FloatRange | null;
  priceGap?: FloatRange | null;
  turnover?: IntegerRange | null;
  numberOfSeats?: IntegerRange | null;
  vatReclaimable?: boolean | null;
  carvagoAvailability?: boolean | null;
  adTitleSearchQuery?: string | null;
  location?: LocationRange | null;
  includeSuspicious?: boolean;
  includeBlacklist?: boolean;
  onlyHitlist?: boolean;
  filterPresetId?: string | null;
};

export type SourcingVehicleListResponseBody = {
  data: SourcingVehicleListItemResponseBody[];
  links: UserListLinksResponseBody;
  /** Client-only RTK cache field: highest page-aligned offset attempted via infinite scroll. */
  scrollAttemptedOffset?: number;
  /** Client-only RTK cache field: list endpoint returned fewer than ROWS_PER_PAGE (no more server pages). */
  isEndReached?: boolean;
};

export type SourcingVehicleListApiResponse = /** status 200  */ SourcingVehicleListResponseBody;
export type SourcingVehicleListApiArg = {
  page?: number;
  currency?: Alpha3;
  orderBy?: string | string[] | null;
  body: AvailableCarsFilterRequestBody;
};

export type CarFeaturesResponseBody = {
  featuresScore: number;
  premiumFeatures: string[];
  highlightedFeatures: string[];
};
export type CarFeaturesRequestBody = {
  make: string;
  model: string;
  year: number;
  features: string[];
  compare?: boolean;
  size?: number;
  power?: number;
};
export type GetCarFeaturesApiResponse = /** status 200  */ CarFeaturesResponseBody;
export type GetCarFeaturesApiArg = {
  body: CarFeaturesRequestBody;
};

export type SourcingVehicleCountResponseBody = {
  count: number;
};
export type GetSourcingVehicleCountApiResponse =
  /** status 200  */ SourcingVehicleCountResponseBody;
export type GetSourcingVehicleCountApiArg = {
  currency?: Alpha3;
  body: AvailableCarsFilterRequestBody;
};

export type SourcingColorResponseBody = {
  color: TranslationResponseBody | null;
  specification: TranslationResponseBody | null;
};

export type ExtraDataResponseBody = {
  marketPosition: number | null;
  marketPositionCount: number | null;
  maximalPossibleMargin: number | null;
  predictedPrice: number | null;
};

export type FeatureResponseBody = {
  key: string;
  isHighlight: boolean;
  translation: string;
};

export type HighlightResponseBody = {
  id: string;
  title: string;
};

export type OccurrenceResponseBody = {
  first: string | null;
  last: string | null;
};

export type SourcingPriceResponseBody = {
  withVat: number | null;
  vatRate: number | null;
  vatReclaimable: boolean | null;
  currency: TranslationResponseBody | null;
  originalCurrency: TranslationResponseBody | null;
  originalWithVat: number | null;
};

export type RatingResponseBody = {
  average: number | null;
  count: number | null;
};

export type SellerResponseBody = {
  name: string | null;
  type: TranslationResponseBody | null;
  address: string | null;
  city: string | null;
  zip: string | null;
  email: string | null;
  phone: string | null;
  rating: RatingResponseBody | null;
  country: TranslationResponseBody | null;
};

export type ShortcomingResponseBody = {
  id: string;
  title: string;
};

export type SimilarityResponseBody = {
  level: number | null;
  score: number | null;
};

export type UrlResponseBody = {
  full: string | null;
};

export type SourcingVehicleResponseBody = {
  id: string;
  adId: string;
  isAvailable: boolean;
  isSold: boolean | null;
  carStyle: TranslationResponseBody | null;
  color: SourcingColorResponseBody | null;
  cubicCapacity: number | null;
  daysOnStock: number | null;
  doorCount: TranslationResponseBody | null;
  driveType: TranslationResponseBody | null;
  featureScore: number | null;
  features: TranslationResponseBody[];
  trimLevel: string | null;
  fuelType: TranslationResponseBody | null;
  highlightFeatures: TranslationResponseBody[];
  interiorMaterial: TranslationResponseBody | null;
  make: TranslationResponseBody | null;
  mileage: number | null;
  model: TranslationResponseBody | null;
  modelFamilyGroup: TranslationResponseBody | null;
  numberOfSeats: number | null;
  occurrence: OccurrenceResponseBody | null;
  originCountry: TranslationResponseBody | null;
  power: number | null;
  price: SourcingPriceResponseBody | null;
  seller: SellerResponseBody | null;
  server: string | null;
  similarity: SimilarityResponseBody | null;
  technicalInspectionValidTo: string | null;
  thumbnailUrl: UrlResponseBody | null;
  transmission: TranslationResponseBody | null;
  turnover: number | null;
  url: UrlResponseBody | null;
  proxyUrl: UrlResponseBody | null;
  vin: string | null;
  year: number | null;
  maximalPossibleMargin: number | null;
  extraData: ExtraDataResponseBody | null;
  adTitle: string | null;
  description: string | null;
  pictureUrls: string[];
  suspicious: boolean;
  suspiciousReason: (
    | 'suspicious_price_low'
    | 'suspicious_price_high'
    | 'suspicious_mileage_low'
    | 'suspicious_mileage_high'
    | 'suspicious_dod'
  )[];
  attractivenessScore: number | null;
  marketSituationCategory:
    | (
        | 'MARKET_SITUATION_MAJOR_SHORTAGE'
        | 'MARKET_SITUATION_SURPLUS'
        | 'MARKET_SITUATION_STABLE'
        | 'MARKET_SITUATION_SHORTAGE'
      )
    | null;
  priceDynamicsCategory:
    | ('PRICE_DYNAMICS_FALLING' | 'PRICE_DYNAMICS_RISING' | 'PRICE_DYNAMICS_STABLE')
    | null;
  isBlacklist: boolean;
  isHitlist: boolean;
  priceChanges: number[];
  priceChangesDates: string[];
  pricePercentile: number | null;
};

export type SummaryResponseBody = {
  position: number | null;
  positionFrom: number | null;
  margin: number | null;
  original: number | null;
  purchase: number | null;
  retail: number | null;
  lid: number | null;
  dod: number | null;
  rank: number | null;
  vehicleCosts: number | null;
  adPrice: number | null;
};
export type VehicleSummaryResponseBody = {
  id?: string | null;
  purchasePrice: number | null;
  sellingPrice: number | null;
  comment: string | null;
};
export type SourcingVehicleDetailResponseBody = {
  favourite: boolean;
  comparison: boolean;
  seen: boolean;
  isNew?: boolean; // Always undefined in detail responses (only present in list responses)
  inCart: boolean;
  hidden: boolean;
  carvagoBuy: boolean;
  summary: SummaryResponseBody;
  vehicleSummary: VehicleSummaryResponseBody | null;
  sourcingVehicle: SourcingVehicleResponseBody;
  features: {
    [key: string]: FeatureResponseBody;
  };
  highlights: HighlightResponseBody[];
  shortcomings: ShortcomingResponseBody[];
};

export type SourcingVehicleDetailApiResponse = /** status 200  */ SourcingVehicleDetailResponseBody;
export type SourcingVehicleDetailApiArg = {
  externalId: string;
  marginLte?: number | null;
  marginGte?: number | null;
  positionGte?: number | null;
  positionLte?: number | null;
  currency?: Alpha3;
  buyerCountry?: Alpha3;
};

export type TopBarResponseBody = {
  countries: Alpha3[];
  currencies: Alpha3[];
};
export type AllowedCountryAndCurrencyApiResponse = /** status 200  */ TopBarResponseBody;
export type AllowedCountryAndCurrencyApiArg = void;

export type ListUserResponseBody = {
  data: SourcingVehicleDetailResponseBody[];
  links: UserListLinksResponseBody;
};
export type ListUserFavouriteApiResponse = /** status 200  */ ListUserResponseBody;
export type ListUserFavouriteApiArg = {
  size?: number;
  offset?: number;
  currency?: Alpha3;
  buyerCountry?: Alpha3;
  margin?: number;
  orderBy?: string;
};

export type ListUserHiddenApiResponse = /** status 200  */ ListUserResponseBody;
export type ListUserHiddenApiArg = {
  size?: number;
  offset?: number;
  currency?: Alpha3;
  buyerCountry?: Alpha3;
  margin?: number;
  orderBy?: string;
};

export type ListUserToBuyApiResponse = /** status 200  */ ListUserResponseBody;
export type ListUserToBuyApiArg = {
  size?: number;
  offset?: number;
  currency?: Alpha3;
  buyerCountry?: Alpha3;
  margin?: number;
  orderBy?: string;
};

export type ListUserComparisonApiResponse = /** status 200  */ ListUserResponseBody;
export type ListUserComparisonApiArg = {
  size?: number;
  offset?: number;
  currency?: Alpha3;
  buyerCountry?: Alpha3;
  margin?: number;
  orderBy?: string;
};

export type BulkDeleteUserFavouriteRequestBody = {
  externalIds: string[];
};
export type BulkCreateUserFavouriteRequestBody = {
  externalIds: string[];
};
export type BulkDeleteUserFavouriteApiResponse = unknown;
export type BulkDeleteUserFavouriteApiArg = {
  body: BulkDeleteUserFavouriteRequestBody;
};
export type BulkCreateUserFavouriteApiResponse = unknown;
export type BulkCreateUserFavouriteApiArg = {
  body: BulkCreateUserFavouriteRequestBody;
};
export type BulkDeleteUserComparisonRequestBody = {
  externalIds: string[];
};
export type BulkCreateUserComparisonRequestBody = {
  externalIds: string[];
};
export type BulkDeleteUserComparisonApiResponse = unknown;
export type BulkDeleteUserComparisonApiArg = {
  body: BulkDeleteUserComparisonRequestBody;
};
export type BulkCreateUserComparisonApiResponse = unknown;
export type BulkCreateUserComparisonApiArg = {
  body: BulkCreateUserComparisonRequestBody;
};
export type BulkDeleteUserHiddenRequestBody = {
  externalIds: string[];
};
export type BulkCreateUserHiddenRequestBody = {
  externalIds: string[];
};
export type BulkDeleteUserHiddenApiResponse = unknown;
export type BulkDeleteUserHiddenApiArg = {
  body: BulkDeleteUserHiddenRequestBody;
};
export type BulkCreateUserHiddenApiResponse = unknown;
export type BulkCreateUserHiddenApiArg = {
  body: BulkCreateUserHiddenRequestBody;
};
export type BulkDeleteUserToBuyRequestBody = {
  externalIds: string[];
};
export type BulkCreateUserToBuyRequestBody = {
  externalIds: string[];
};
export type BulkDeleteUserToBuyApiResponse = unknown;
export type BulkDeleteUserToBuyApiArg = {
  body: BulkDeleteUserToBuyRequestBody;
};
export type BulkCreateUserToBuyApiResponse = unknown;
export type BulkCreateUserToBuyApiArg = {
  body: BulkCreateUserToBuyRequestBody;
};

export type DrillDownLevelName =
  | 'make'
  | 'model_family_group'
  | 'model'
  | 'year'
  | 'power'
  | 'fuel_type'
  | 'transmission'
  | 'drive'
  | 'mileage_range'
  | 'car_style';

export type DrillDownLevel = {
  level: DrillDownLevelName;
  values: string[];
  priority: number;
};

export type DrillDownStatisticsLevel = {
  name: DrillDownLevelName;
  value: string;
};

export type DrillDownStatisticsItem = {
  type: 'sold' | 'stock';
  amount: number;
  mileage: number;
  price: number;
};

export type DrillDownApiArg = {
  levels: DrillDownLevel[];
  currency: string;
  regions: string[];
};
export type DrillDownStatistics = {
  levels: DrillDownStatisticsLevel[];
  statistics: DrillDownStatisticsItem[];
};
export type DrillDownResponse = {
  translations: Record<string, string>;
  statisticsAggregated: DrillDownStatistics[];
};

export type CreateUserVehicleSummaryRequestBody = {
  comment?: string | null;
  purchasePrice?: number | null;
  sellingPrice?: number | null;
};
export type CreateUserVehicleSummaryApiArg = {
  sourcingVehicleId: string;
  body: CreateUserVehicleSummaryRequestBody;
};
export type SourcingUser = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
};
export type SalesClaim = {
  id: string;
  note: string;
  author: SourcingUser;
  createdAt: string;
};
export type SimilarVehicleRequestBody = {
  margin?: number;
  make: string;
  model: string;
  year: number;
  carStyle: string;
  currency: string;
  fuelType: string;
  sellerCountry: string;
  power: number;
  drive: string;
  transmission: string;
  mileage: number;
};
type Summary = {
  position: number | null;
  positionFrom: number | null;
  positionPercentage: number;
  margin: number | null;
  original: number | null;
  purchase: number | null;
  retail: number | null;
  selected: number | null;
  lid: number | null;
  dod: number | null;
  rank: number | null;
  vehicleCosts: number | null;
  adPrice: number | null;
};
type Feature = {
  id?: string;
  key: string;
  isHighlighted?: boolean;
  translation?: string;
};
type SimilarSourcingVehicleDetail = {
  favourite: boolean;
  comparison: boolean;
  seen: boolean;
  inCart: boolean;
  hidden: boolean;
  summary: Summary;
  vehicleSummary: VehicleSummaryResponseBody | null;
  sourcingVehicle: SourcingVehicleResponseBody;
  features: Record<string, Feature>;
};
type SimilarVehicleStatisticsGroup = {
  key: string;
  count: number;
  avgFeatureScore?: number | null;
  avgMileage?: number | null;
  avgPrice?: number | null;
};
export type SimilarVehicleList = {
  onStock: Array<SimilarSourcingVehicleDetail>;
  soldRecently: Array<SimilarSourcingVehicleDetail>;
  sold: Array<SimilarSourcingVehicleDetail>;
};
export type SimilarVehicleStatistics = {
  groups: Array<SimilarVehicleStatisticsGroup>;
};
type PriceMapVehiclesTypeKey = 'onStock' | 'soldRecently' | 'sold';
export type VehicleTypeForPriceMap = {
  price?: number;
  mileage?: number;
  type?: PriceMapVehiclesTypeKey;
} & SourcingVehicleDetailResponseBody;
export type PriceMapStatistics = {
  avgFeatureScore: number;
  avgMileage: number;
  avgPrice: number;
  count: number;
  key: string;
};
export type SourcingServer = {
  code: string;
  name: string;
  preferred: boolean;
};
export type SortUserComparisonRequestApiArg = SourcingVehicleDetailResponseBody[];
type ExternalIdWithPositionRequestBody = {
  externalId: string;
  position: number;
};
export type SortUserComparisonRequestBody = ExternalIdWithPositionRequestBody[];
export type PushVehiclesToSaleApiArg = {
  externalIds: string[];
};
export type ExportVehiclesToCsvApiArg = {
  externalIds: string[];
  buyerCountry?: Alpha3;
  currency?: Alpha3;
  margin?: string;
  orderBy?: string;
};
export type SourcingCommentUser = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  external: boolean;
};
export type SourcingComment = {
  uuid: string;
  message: string;
  parent: {
    uuid: string;
  } | null;
  author: SourcingCommentUser;
  createdAt: string;
};
export type SourcingGetCommentsApiResponse = SourcingComment[];
export type SourcingGetCommentsApiArg = {
  externalId: string;
};
export type SourcingCreateCommentApiResponse = SourcingGetCommentsApiResponse;
export type SourcingCreateCommentRequestBody = {
  message: string;
  parentCommentUuid?: string | null;
};
export type SourcingCreateCommentApiArg = {
  externalId: string;
  body: SourcingCreateCommentRequestBody;
};
export type SourcingDeleteCommentApiArg = {
  externalId: string;
  uuid: string;
};
export type SourcingUpdateCommentApiResponse = SourcingGetCommentsApiResponse;
export type SourcingUpdateCommentRequestBody = SourcingCreateCommentRequestBody;
export type SourcingUpdateCommentApiArg = {
  externalId: string;
  uuid: string;
  body: SourcingUpdateCommentRequestBody;
};
export type DetailUserCostVehicleNoteApiResponse = /** status 200  */ CostVehicleNoteResponseBody;
export type DetailUserCostVehicleNoteApiArg = {
  sourcingVehicleId: string;
};
export type SetUserVehicleCostNoteApiArg = {
  sourcingVehicleId: string;
  updateCostVehicleNoteRequestBody: UpdateCostVehicleNoteRequestBody;
};
export type CostVehicleNoteResponseBody = {
  uuid: string;
  note: string;
};
export type UpdateCostVehicleNoteRequestBody = {
  note: string;
};
export type DetailDefaultUserPresetCostApiResponse =
  /** status 200  */ CostPresetDetailResponseBody;
export type CreateUserVehicleCostApiArg = {
  sourcingVehicleId: string;
  userVehicleCostRequestBody: UserVehicleCostRequestBody;
};
export type CreateUserVehicleCostsApiArg = {
  sourcingVehicleId: string;
  userVehicleCostsRequestBody: UserVehicleCostsRequestBody;
};
export type UserCostVehicleListApiResponse = /** status 200  */ CostDetailItemResponseBody[];
export type UserCostVehicleListApiArg = {
  sourcingVehicleId: string;
};
export type DeleteUserVehicleCostApiArg = {
  sourcingVehicleId: string;
  itemUuid: string;
};
export type UpdateUserVehicleCostApiArg = {
  sourcingVehicleId: string;
  itemUuid: string;
  userVehicleCostRequestBody: UserVehicleCostRequestBody;
};
export type CostDetailItemResponseBody = {
  uuid: string;
  title: string;
  type: 'fixed' | 'other' | 'important';
  cost: number | null;
  currency: string | null;
  note: string | null;
  enabled: boolean;
};
export type CostPresetDetailResponseBody = {
  uuid: string;
  title: string;
  note: string | null;
  default: boolean;
  presetItems: CostDetailItemResponseBody[];
};
export type CostItem = {
  title: string;
  type: 'fixed' | 'other' | 'important';
  cost: string;
  currency: string;
  note: string;
  enabled: boolean;
};
export type UserVehicleCostRequestBody = {
  costItem: CostItem;
};
export type UserVehicleCostsRequestBody = {
  costItems: CostItem[];
};
export type RemoveHighlightBulkApiResponse = /** status 204  */ void;
export type RemoveHighlightBulkApiArg = {
  externalId: string;
  removeHighlightBulkRequestBody: RemoveHighlightBulkRequestBody;
};
export type RemoveShortcomingBulkApiResponse = /** status 204  */ void;
export type RemoveShortcomingBulkApiArg = {
  externalId: string;
  removeShortcomingBulkRequestBody: RemoveShortcomingBulkRequestBody;
};
export type RemoveShortcomingBulkRequestBody = {
  shortcomingIds: string[];
};
export type RemoveHighlightBulkRequestBody = {
  highlightIds: string[];
};

export type AdServerRedirectApiArg = {
  externalId?: string;
  serverName?: string;
  buyerCountry?: Alpha3;
  sellerCountry?: Alpha3;
  currency?: Currency;
  relativeFilterId?: string | null;
};
export type AdServerRedirectResponse = {
  url: string;
};
export type CreateRelativeFilterApiResponse = /** status 201  */ UuidResponseBody;
export type CreateRelativeFilterApiArg = {
  createRelativeFilterRequestBody: CreateRelativeFilterRequestBody;
};
export type GetRelativeFiltersApiResponse = /** status 200  */ RelativeFilterResponseBody[];
export type GetRelativeFiltersApiArg = void;
export type DeleteRelativeFilterApiResponse = /** status 204  */ void;
export type DeleteRelativeFilterApiArg = {
  id: string;
};
export type UpdateRelativeFilterApiResponse = /** status 204  */ void;
export type UpdateRelativeFilterApiArg = {
  id: string;
  updateRelativeFilterRequestBody: UpdateRelativeFilterRequestBody;
};
export type ReorderRelativeFiltersApiResponse = /** status 204  */ void;
export type ReorderRelativeFiltersApiArg = {
  reorderRelativeFiltersRequestBody: ReorderRelativeFiltersRequestBody;
};
export type NumericBoundBody = {
  from: number | null;
  to: number | null;
};
export type RelativeFilterChoice = 'same' | 'same_better';
export type RelativeFilterConfigBody = {
  year: NumericBoundBody | null;
  mileage: NumericBoundBody | null;
  mileagePct: NumericBoundBody | null;
  power: NumericBoundBody | null;
  powerPct: NumericBoundBody | null;
  price: NumericBoundBody | null;
  pricePct: NumericBoundBody | null;
  featureScore: NumericBoundBody | null;
  trimLevel: RelativeFilterChoice | null;
  transmission: RelativeFilterChoice | null;
  fuel: RelativeFilterChoice | null;
  body: RelativeFilterChoice | null;
  drive: RelativeFilterChoice | null;
  vatDeductible: RelativeFilterChoice | null;
  sellerType: RelativeFilterChoice | null;
  seats: RelativeFilterChoice | null;
  radius: number | null;
};
export type CreateRelativeFilterRequestBody = {
  name: string;
  config: RelativeFilterConfigBody;
};
export type RelativeFilterResponseBody = {
  id: string;
  name: string;
  config: RelativeFilterConfigBody;
  position: number;
  system: boolean;
  createdAt: string;
  updatedAt: string;
};
export type UpdateRelativeFilterRequestBody = {
  name: string;
  config: RelativeFilterConfigBody;
};
export type OrderedRelativeFilterItem = {
  id: string;
  position: number;
};
export type ReorderRelativeFiltersRequestBody = {
  items: OrderedRelativeFilterItem[];
};
export type LocationRange = {
  lat: number;
  lon: number;
  distance: number;
};
export type CreateUserSeenApiResponse = unknown;
export type CreateUserSeenApiArg = {
  body: CreateUserSeenRequestBody;
};
export type CreateUserSeenRequestBody = {
  externalId: string;
};
export type GetPriceMapApiResponse = /** status 200  */ PriceMapResponseBody;
export type GetPriceMapApiArg = {
  externalId: string;
  priceMapRequestBody: PriceMapRequestBody;
};
export type LocationResponseBody = {
  lat?: number | null;
  lon?: number | null;
};
export type PriceMapCarResponseBody = {
  ad_id: string | null;
  days_on_stock: number | null;
  feature_score: number | null;
  first_scraped_price_currency: string | null;
  first_scraped_price_with_vat: number | null;
  is_available: boolean | null;
  is_sold: boolean | null;
  last_occurrence: string | null;
  location: LocationResponseBody | null;
  mileage: number | null;
  original_currency: string | null;
  original_price_with_vat: number | null;
  price_with_vat: number | null;
  suspicious: boolean | null;
};
export type PriceMapResponseBody = {
  total_hits: number | null;
  total_pages: number | null;
  current_page: number | null;
  query_price_position: number | null;
  query_price_index: number | null;
  query_original_price_index: number | null;
  cars: PriceMapCarResponseBody[];
};
export type IntegerRangeFilter = {
  gte?: number | null;
  lte?: number | null;
};
export type FloatRangeFilter = {
  gte?: number | null;
  lte?: number | null;
};
export type LocationFilter = {
  lat?: number | null;
  lon?: number | null;
  distance?: number | null;
};
export type PriceMapRequestBody = {
  size: number;
  query_price: number;
  offset?: number | null;
  fuelType?: string[] | null;
  transmission?: string[] | null;
  bodyStyle?: string[] | null;
  drive?: string[] | null;
  mileage?: IntegerRangeFilter | null;
  firstRegistrationOnYear?: IntegerRangeFilter | null;
  power?: IntegerRangeFilter | null;
  price?: IntegerRangeFilter | null;
  daysOnStock?: IntegerRangeFilter | null;
  featuresLevel?: FloatRangeFilter | null;
  trimLevel?: string[] | null;
  isVatDeductible?: boolean | null;
  hideSuspiciousCars?: boolean | null;
  hideSoldCars?: boolean | null;
  hideStockCars?: boolean | null;
  hideSoldIn30?: boolean | null;
  hideSoldIn90?: boolean | null;
  location?: LocationFilter | null;
  title?: string | null;
};
export type SourcingFilterpresetsPresetNotificationsDisableApiResponse = unknown;
export type SourcingFilterpresetsPresetNotificationsDisableApiArg = {
  id: string;
};
export type SourcingFilterpresetsPresetNotificationsEnableApiResponse = unknown;
export type SourcingFilterpresetsPresetNotificationsEnableApiArg = {
  id: string;
};
export type CreateSourcingVehicleOfferApiResponse =
  /** status 200  */ SourcingVehicleOfferResponseBody;
export type CreateSourcingVehicleOfferApiArg = {
  createSourcingVehicleOfferRequestBody: CreateSourcingVehicleOfferRequestBody;
};
export type GetSourcingVehicleOfferApiResponse =
  /** status 200  */ SourcingVehicleOfferResponseBody;
export type GetSourcingVehicleOfferApiArg = {
  offerId: string;
};
export type SourcingVehicleOfferResponseBody = {
  id: string;
  externalId: string;
};
export type CreateSourcingVehicleOfferRequestBody = {
  externalId: string;
};

export type BulkDeleteUserCarvagoBuyApiResponse = unknown;
export type BulkDeleteUserCarvagoBuyApiArg = {
  bulkDeleteUserCarvagoBuyRequestBody: BulkDeleteUserCarvagoBuyRequestBody;
};
export type BulkCreateUserCarvagoBuyApiResponse = /** status 204  */ void;
export type BulkCreateUserCarvagoBuyApiArg = {
  bulkCreateUserCarvagoBuyRequestBody: BulkCreateUserCarvagoBuyRequestBody;
};
export type DeleteUserCarvagoBuyApiResponse = unknown;
export type DeleteUserCarvagoBuyApiArg = {
  externalId: string;
};
export type ListUserCarvagoBuyApiResponse = /** status 200  */ ListUserResponseBody;
export type ListUserCarvagoBuyApiArg = {
  size?: number;
  offset?: number;
  currency?: Alpha3;
  buyerCountry?: Alpha3;
  margin?: number;
  orderBy?: string;
};
export type CreateUserCarvagoBuyApiResponse = /** status 204  */ void;
export type CreateUserCarvagoBuyApiArg = {
  createUserCarvagoBuyRequestBody: CreateUserCarvagoBuyRequestBody;
};
export type BulkDeleteUserCarvagoBuyRequestBody = {
  externalIds: string[];
};
export type BulkCreateUserCarvagoBuyRequestBody = {
  externalIds: string[];
};
export type CreateUserCarvagoBuyRequestBody = {
  externalId: string;
};
